import { Button, Divider, InputNumber } from 'antd';
import SafeImage from 'components/SafeImage';
import useModal from 'hooks/useModal';
import SubSkusModal from './SubSkusModal';
import { useFormikContext } from 'formik';
import { Flex, FV } from './share';
import styled from '@emotion/styled';
import FormField from 'components/FormField';
import { useTranslate } from '@tolgee/react';

function ErpSku({ index, erpItemSku }: { index: number; erpItemSku?: Todo.ErpItemSku }) {
  const disabled = !erpItemSku;

  const { t } = useTranslate();

  const {
    values,
    values: { currency, warehouseName, skus = [] },
    setValues,
  } = useFormikContext<FV>();

  const sku = skus[index];

  const onSkuChange = (sku: Todo.ErpOrderSku) =>
    setValues({
      ...values,
      skus: [...skus.slice(0, index), sku, ...skus.slice(index + 1)],
    });

  const { imgUrl, skuName, skuId, skuQuantity, skuAmount, subSkus } = sku;

  const inventory = erpItemSku?.inventories.find((_) => _.warehouseName === warehouseName);

  const { visible, show, hide } = useModal();

  const getSubSkusInvalid = (subSkus: Todo.ErpOrderSku[]) => {
    return (
      !disabled &&
      subSkus.some(({ skuId, skuQuantity }) => {
        const target = erpItemSku?.subSkus?.find((_) => _.skuId === skuId);
        const inventory = target?.inventories.find((_) => _.warehouseName === warehouseName);
        return (
          (warehouseName && !inventory) ||
          (inventory && skuQuantity && inventory.inventory < skuQuantity)
        );
      })
    );
  };

  const subSkusInvalid = getSubSkusInvalid(subSkus);

  const getSkuInvalid = (sku: Todo.ErpOrderSku) =>
    !disabled &&
    sku.subSkus.length === 0 &&
    ((warehouseName && !inventory) ||
      (inventory && sku.skuQuantity && sku.skuQuantity > inventory.inventory));

  return (
    <Container>
      <FormField<Required<FV>['skus'][0]>
        name={`skus.${index}`}
        label={
          disabled
            ? t('ERP系统商品')
            : `${t('ERP系统商品')}（${t('将使用此系统商品创建补发单')}）：`
        }
        validate={(sku) => {
          if (getSubSkusInvalid(sku.subSkus)) {
            return t('创建失败，补发件数超过发货仓库存');
          }
          if (getSkuInvalid(sku)) {
            return t('创建失败，补发件数超过发货仓库存');
          }
        }}
      >
        <Item style={subSkusInvalid ? { borderColor: '#FF4747' } : undefined}>
          <SafeImage src={imgUrl} width={64} height={64} />
          <section>
            <p>
              {subSkus.length > 0 && (
                <span>{t('scene.chatWorkspace.taskTab.erpOrderForm.unit.group')}</span>
              )}
              {skuName}
            </p>
            <div>
              <p>
                {t('商品编码')}：{skuId}
                {inventory && (
                  <>
                    <Divider type="vertical" />
                    {t('scene.chatWorkspace.taskTab.erpOrderForm.label.inventory')}：
                    {inventory.inventory}
                  </>
                )}
              </p>
              {Boolean(subSkus.length || erpItemSku?.subSkus?.length) && (
                <Button danger={subSkusInvalid} size="small" type="link" onClick={show}>
                  {subSkusInvalid
                    ? t('发货仓库存不足')
                    : subSkus.length
                    ? `${t('已选择补发子商品')}（${subSkus.length}）`
                    : t('选择补发子商品')}
                  {' >'}
                </Button>
              )}
              <SubSkusModal
                visible={visible}
                onCancel={hide}
                currency={currency}
                warehouseName={warehouseName}
                erpItemSubSkus={erpItemSku?.subSkus}
                subSkus={subSkus}
                onSubSkusChange={(subSkus) =>
                  onSkuChange({
                    ...sku,
                    subSkus,
                  })
                }
              />
            </div>
          </section>
        </Item>
      </FormField>
      {sku.subSkus.length === 0 && (
        <Flex items={[1, 1]}>
          <FormField
            name={`skus.${index}.skuQuantity`}
            required
            label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.pieceCount')}
            validateStatus={getSkuInvalid(sku) ? 'error' : undefined}
            validate={(value) => {
              if (!value && value !== 0) {
                return t('common.validation.requiredInput', {
                  fieldName: t('scene.chatWorkspace.taskTab.erpOrderForm.label.pieceCount'),
                });
              }
              // if (sku.subSkus.length === 0 && value && inventory && value > inventory.inventory) {
              //   return ' ';
              // }
            }}
          >
            <InputNumber
              disabled={disabled}
              precision={0}
              min={1}
              value={skuQuantity}
              onChange={(value) =>
                onSkuChange({
                  ...sku,
                  skuQuantity: value as any,
                })
              }
              addonAfter={t('scene.chatWorkspace.taskTab.erpOrderForm.unit.piece')}
            />
          </FormField>
          <FormField
            name={`skus.${index}.skuAmount`}
            required
            label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.amount')}
            validate={(value) => (value ? undefined : t('请输入金额'))}
          >
            <InputNumber
              disabled={disabled}
              min={0}
              value={skuAmount ? Number(skuAmount) : undefined}
              onChange={(value) =>
                onSkuChange({
                  ...sku,
                  skuAmount: typeof value === 'number' ? value.toString() : '',
                })
              }
              addonAfter={currency}
            />
          </FormField>
        </Flex>
      )}
    </Container>
  );
}

const Container = styled.div`
  > .ant-form-item {
    .ant-form-item-explain {
      opacity: 0;
    }
  }
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  background: #fff;
  border: solid 1px #d3d7e0;
  > div {
    flex: 0 0 auto;
    margin-right: 10px;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    overflow: hidden;
    > p {
      margin-bottom: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      > span {
        display: inline-block;
        line-height: 20px;
        margin-right: 4px;
        padding: 0 4px;
        color: #fff;
        background: var(--ant-primary-color);
        border-radius: 2px;
      }
    }
    > div {
      display: flex;
      justify-content: space-between;
      align-items: center;
      > p {
        flex: 1 0 0;
        margin-bottom: 0;
        font-size: 12px;
        color: #84868c;
      }
    }
  }
`;

export default ErpSku;
